import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { updateTermsAgreement } from "../utils/terms";
import ElectronicSignature from "./ElectronicSignature";
import ElectronicSignature2 from "./ElectronicSignature2";
import ElectronicSignature3 from "./ElectronicSignature3";

// 동의서 전문 내용을 담을 상수들

const AgreementPage = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [selectedAgreements, setSelectedAgreements] = useState({
    all: false,
    contract: false,
    mandate: false,
    signature: false,
  });
  const [currentComponent, setCurrentComponent] = useState(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const buttonRef = useRef(null);

  // 전체 동의 처리
  const handleAllAgreements = () => {
    const newValue = !selectedAgreements.all;
    setSelectedAgreements({
      all: newValue,
      contract: newValue,
      mandate: newValue,
      signature: newValue,
    });
  };

  // 개별 동의 처리
  const handleAgreementToggle = (key) => {
    const newState = {
      ...selectedAgreements,
      [key]: !selectedAgreements[key],
    };

    // 모든 항목이 선택되었는지 확인하여 '전체 동의' 상태 업데이트
    const allSelected =
      newState.contract && newState.mandate && newState.signature;

    setSelectedAgreements({
      ...newState,
      all: allSelected,
    });
  };

  // 컴포넌트 모달 열기
  const openComponent = (componentType) => {
    setCurrentComponent(componentType);
  };

  // 컴포넌트 모달 닫기
  const closeComponent = () => {
    setCurrentComponent(null);
  };

  // 서명 방법 선택 모달 열기
  const openSignModal = () => {
    setShowSignModal(true);
  };

  // 서명 방법 선택 모달 닫기
  const closeSignModal = () => {
    setShowSignModal(false);
  };

  // 카카오 전자서명 선택 시
  const handleKakaoSign = () => {
    closeSignModal();
    navigate("/kakao-auth");
  };

  // 모두의 사인 선택 시
  const handleModuSign = () => {
    closeSignModal();
    navigate("/modusign-auth");
  };

  // 전자서명 동의 완료 처리
  const handleAgreementComplete = (type) => {
    let agreementKey;
    switch (type) {
      case "contract":
        agreementKey = "contract";
        break;
      case "mandate":
        agreementKey = "mandate";
        break;
      case "signature":
        agreementKey = "signature";
        break;
      default:
        agreementKey = null;
    }

    if (agreementKey) {
      setSelectedAgreements((prev) => ({
        ...prev,
        [agreementKey]: true,
        all:
          prev.all ||
          ((agreementKey === "contract" ? true : prev.contract) &&
            (agreementKey === "mandate" ? true : prev.mandate) &&
            (agreementKey === "signature" ? true : prev.signature)),
      }));
    }

    closeComponent();
  };

  // 필수 항목 모두 동의했는지 확인 및 버튼 활성화 상태 업데이트
  const isAllRequiredAgreed = () => {
    return (
      selectedAgreements.contract &&
      selectedAgreements.mandate &&
      selectedAgreements.signature
    );
  };

  // 확인하기 버튼 - 소송위임계약서/소송위임장/전자서명 동의를 서버에 반영 후 다음 단계로 이동
  const handleConfirm = async () => {
    await updateTermsAgreement({
      hasReviewedLitigationRetainerAgreement: true,
      hasReviewedLitigationPowerOfAttorney: true,
      hasAgreedToElectronicProxy: true,
    });
    navigate("/signature-pad");
  };

  // 버튼 활성화 상태 감지 및 스크롤 처리
  useEffect(() => {
    const buttonEnabled = isAllRequiredAgreed();

    // 버튼 상태가 비활성화에서 활성화로 변경될 때만 스크롤
    if (buttonEnabled && !isButtonEnabled && buttonRef.current) {
      setTimeout(() => {
        buttonRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 300);
    }

    setIsButtonEnabled(buttonEnabled);
  }, [selectedAgreements, isButtonEnabled]);

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  const modalVariants = {
    initial: {
      opacity: 0,
      scale: 0.8,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2,
      },
    },
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <Header>
            <IconContainer>
              <IconImage src="/png/agreementIcon.png" alt="동의서 아이콘" />
            </IconContainer>
            <SubTitle>약관에 동의해 주세요</SubTitle>
            <Description>
              집단소송 참여에 필요한
              <br />
              동의사항을 확인해주세요
            </Description>
          </Header>

          <AgreementList>
            <AgreementItem
              onClick={(e) => {
                e.stopPropagation();
                openComponent("contract");
              }}
            >
              <CheckboxContainer>
                <CheckboxImage
                  src={
                    selectedAgreements.contract
                      ? "/png/checked.png"
                      : "/png/checkedinActive.png"
                  }
                  alt="체크박스"
                />
              </CheckboxContainer>
              <AgreementText>
                소송위임계약서 확인 <RequiredBadge>[필수]</RequiredBadge>
              </AgreementText>
              <ViewButton
                onClick={(e) => {
                  e.stopPropagation();
                  openComponent("contract");
                }}
              >
                <img src="/png/mingcute_down-line.png" alt="상세보기" />
              </ViewButton>
            </AgreementItem>

            <AgreementItem
              onClick={(e) => {
                e.stopPropagation();
                openComponent("mandate");
              }}
            >
              <CheckboxContainer>
                <CheckboxImage
                  src={
                    selectedAgreements.mandate
                      ? "/png/checked.png"
                      : "/png/checkedinActive.png"
                  }
                  alt="체크박스"
                />
              </CheckboxContainer>
              <AgreementText>
                소송위임장 확인 <RequiredBadge>[필수]</RequiredBadge>
              </AgreementText>
              <ViewButton
                onClick={(e) => {
                  e.stopPropagation();
                  openComponent("mandate");
                }}
              >
                <img src="/png/mingcute_down-line.png" alt="상세보기" />
              </ViewButton>
            </AgreementItem>

            <AgreementItem
              onClick={(e) => {
                e.stopPropagation();
                openComponent("signature");
              }}
            >
              <CheckboxContainer>
                <CheckboxImage
                  src={
                    selectedAgreements.signature
                      ? "/png/checked.png"
                      : "/png/checkedinActive.png"
                  }
                  alt="체크박스"
                />
              </CheckboxContainer>
              <AgreementText>
                전자서명 이미지 사용 및 활용 동의서
                <RequiredBadge>[필수]</RequiredBadge>
              </AgreementText>
              <ViewButton
                onClick={(e) => {
                  e.stopPropagation();
                  openComponent("signature");
                }}
              >
                <img src="/png/mingcute_down-line.png" alt="상세보기" />
              </ViewButton>
            </AgreementItem>
          </AgreementList>

          <ButtonsContainer ref={buttonRef}>
            <ActionButton
              onClick={handleConfirm}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={!isButtonEnabled}
            >
              확인하기
            </ActionButton>
          </ButtonsContainer>
        </Content>

        <AnimatePresence>
          {currentComponent === "contract" && (
            <ElectronicSignature
              onClose={closeComponent}
              onConfirm={() => handleAgreementComplete("contract")}
            />
          )}
          {currentComponent === "mandate" && (
            <ElectronicSignature2
              onClose={closeComponent}
              onConfirm={() => handleAgreementComplete("mandate")}
            />
          )}
          {currentComponent === "signature" && (
            <ElectronicSignature3
              onClose={closeComponent}
              onConfirm={() => handleAgreementComplete("signature")}
            />
          )}
        </AnimatePresence>

        {/* 서명 방법 선택 모달 */}
        <AnimatePresence>
          {showSignModal && (
            <ModalOverlay>
              <SignModal
                variants={modalVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <ModalHeader>
                  <ModalTitle>전자서명 방식 선택</ModalTitle>
                </ModalHeader>
                <ModalContent>
                  <SignOptionContainer onClick={handleKakaoSign}>
                    <SignIconContainer>
                      <SignIcon
                        src="/png/kakaoAuthIcon.png"
                        alt="카카오톡 아이콘"
                      />
                    </SignIconContainer>
                    <SignOptionDetails>
                      <SignOptionTitle>카카오 전자서명</SignOptionTitle>
                      <SignOptionDescription>
                        카카오톡 앱을 통해 간편하게 <br /> 전자서명을
                        진행합니다.
                      </SignOptionDescription>
                    </SignOptionDetails>
                  </SignOptionContainer>

                  <SignOptionContainer onClick={handleModuSign}>
                    <SignIconContainer>
                      <SignIcon
                        src="/png/modusignIcon.png"
                        alt="모두의 사인 아이콘"
                      />
                    </SignIconContainer>
                    <SignOptionDetails>
                      <SignOptionTitle>모두싸인</SignOptionTitle>
                      <SignOptionDescription>
                        모두싸인 서비스를 통해
                        <br /> 전자서명을 진행합니다.
                      </SignOptionDescription>
                    </SignOptionDetails>
                  </SignOptionContainer>
                </ModalContent>
                <ModalFooter>
                  <CancelButton onClick={closeSignModal}>취소</CancelButton>
                </ModalFooter>
              </SignModal>
            </ModalOverlay>
          )}
        </AnimatePresence>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f6f8ff;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    background-color: #f6f8ff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f6f8ff;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);

    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 10px 80px;
    margin-top: 20px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 60px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IconContainer = styled.div`
  width: 150px;
  height: 150px;
  background-color: #eef1f4;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  margin-top: 20px;
  align-self: center;
`;

const IconImage = styled.img`
  width: 150px;
  height: 150px;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  padding-top: 10px;
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  margin-top: 40px;
  font-size: 22px;
  font-weight: 900;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #2e3840;
  text-align: center;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #4b5563;
  line-height: 1.4;
  text-align: center;
`;

const AgreementList = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 0px;
  margin-top: 60px;

  @media (max-width: 768px) {
    margin-bottom: -60px;
    max-width: 100%;
    width: calc(100% + 40px);
  }

  @media (min-width: 769px) {
    margin-bottom: 20px;
  }
`;

const AgreementItem = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  padding: 10px;
  background-color: transparent;
  border-radius: 0px;
  border: none;
  cursor: pointer;
  position: relative;
  opacity: ${(props) => (props.$isRead ? 1 : 0.8)};
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    padding: 10px 20px;
  }

  &:hover {
    background-color: transparent;
  }
`;

const StyledCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  align-self: center;
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
`;

const StyledCheckboxImage = styled.img`
  width: 24px;
  height: 24px;
`;

const AgreementText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  flex: 1;
  margin-right: 10px;
  margin-left: 36px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RequiredBadge = styled.span`
  font-size: 14px;
  color: #3855e2;
  font-weight: 600;
`;

const ViewButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #0075ff;
  font-size: 13px;

  span {
    margin-right: 5px;
  }

  img {
    width: 24px;
    height: 24px;
  }
`;

const ButtonsContainer = styled.div`
  max-width: 400px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 0px;
  padding: 0 0px;
  background-color: #f8f8f8;
  margin: 0 auto;
  padding: 0 10px;

  @media (max-width: 768px) {
    margin-top: 80px;
    padding-bottom: 40px;
  }

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
    justify-content: center;
    margin-top: 20px;
    margin-bottom: 20px;
  }
`;

const ActionButton = styled(motion.button)`
  flex: 1;
  display: flex;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#A9DAFF" : "#3855E2")};
  color: ${(props) => (props.disabled ? "#CCEAFF" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  opacity: 1;
  pointer-events: auto;
`;

// 새로운 스타일 컴포넌트 추가 - 전체 동의 텍스트를 위한 스타일
const AllAgreementText = styled.span`
  font-size: 17px;
  font-weight: 700;
  color: #131313;
  flex: 1;
  margin-left: 36px;
  display: block;
`;

const ReadBadge = styled.span`
  font-size: 12px;
  color: #28a745;
  font-weight: 600;
  margin-left: 8px;
  margin-top: 2px;
  display: block;
  padding-left: 36px;
`;

// 새로운 스타일 컴포넌트 - 약관 섹션 스타일링
const AgreementTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
`;

const AgreementParties = styled.div`
  margin-bottom: 20px;
  line-height: 1.6;

  div {
    margin-bottom: 8px;
  }
`;

const SectionContainer = styled.div`
  margin-bottom: 20px;
`;

const Section = styled.div`
  margin-bottom: 15px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

const SectionContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #444;
  white-space: pre-line;

  h3 {
    font-size: 15px;
    font-weight: 600;
    margin-top: 15px;
    margin-bottom: 8px;
  }

  br {
    margin-bottom: 5px;
  }
`;

const SignatureDate = styled.div`
  margin: 20px 0;
  text-align: center;
  font-weight: 500;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  align-self: center;
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
`;

const CheckboxImage = styled.img`
  width: 24px;
  height: 24px;
`;

const CheckboxLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  margin-left: 10px;
`;

// 테이블 스타일
const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin: 15px 0;
`;

const TableHeader = styled.div`
  background-color: #f0f0f0;
  font-weight: 600;
`;

const TableBody = styled.div``;

const TableRow = styled.div`
  display: flex;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  padding: 8px;
  flex: 1;
  min-width: 80px;
  font-size: 12px;
`;

// 모달 관련 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const SignModal = styled(motion.div)`
  width: 90%;
  max-width: 340px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;
  text-align: center;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ModalContent = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SignOptionContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  border: 1px solid #eee;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f9f9f9;
    border-color: #ddd;
  }
`;

const SignIconContainer = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
`;

const SignIcon = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
`;

const SignOptionDetails = styled.div`
  flex: 1;
`;

const SignOptionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 2px 0;
`;

const SignOptionDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const ModalFooter = styled.div`
  padding: 0 20px 20px;
`;

const CancelButton = styled.button`
  width: 100%;
  height: 48px;
  background-color: #f2f2f2;
  color: #333;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

export default AgreementPage;
